import React from 'react';
import type { OptimizationPreviewData } from '../../types/optimizationPreview';
import { formatPredictedCostUsd, getPredictedCostUsd, pricingModelTooltip } from '../../utils/previewCost';

/** Backend noise for MUTATIONS approach — not shown in preview UI. */
function isHiddenOptimizationPreviewWarning(message: string): boolean {
  const normalized = message.trim().toLowerCase();
  if (!normalized) return true;
  return (
    normalized.includes('mutations usage was estimated') ||
    normalized.includes('expected_tokens formula') ||
    normalized.includes('runner/jobs/v2')
  );
}

function formatNumber(value: number | null | undefined, fractionDigits = 0): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (fractionDigits > 0) {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  }
  return value.toLocaleString();
}

function MetricCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="preview-result-card">
      <div className="preview-result-card-label">{label}</div>
      <div className="preview-result-card-value">{value}</div>
    </div>
  );
}

export default function OptimizationPreviewResultView({ data }: { data: OptimizationPreviewData }) {
  const warnings = Array.isArray(data.warnings)
    ? data.warnings
        .filter((w): w is string => typeof w === 'string' && w.trim().length > 0)
        .filter((w) => !isHiddenOptimizationPreviewWarning(w))
    : [];
  const predictedCost = getPredictedCostUsd(data.predicted_total_cost_usd);
  const pricingTooltip = pricingModelTooltip(data.pricing_model_name);

  return (
    <div className="optimization-preview-result">
      {warnings.length > 0 && (
        <div className="connector-message warning" style={{ marginBottom: 16 }}>
          <i className="fas fa-exclamation-triangle" aria-hidden />{' '}
          {warnings.join(' ')}
        </div>
      )}

      <div className="preview-result-cards" style={{ marginBottom: 16 }}>
        <MetricCard
          label="Predicted cost"
          value={
            <span title={pricingTooltip} className="preview-result-card-value--cost">
              {formatPredictedCostUsd(predictedCost)}
            </span>
          }
        />
        <MetricCard label="Expected input tokens" value={formatNumber(data.expected_input_tokens)} />
        <MetricCard label="Expected output tokens" value={formatNumber(data.expected_output_tokens)} />
        <MetricCard label="Expected total tokens" value={formatNumber(data.expected_total_tokens)} />
        <MetricCard label="Dataset tokens" value={formatNumber(data.dataset_tokens)} />
        <MetricCard label="Avg tokens per turn" value={formatNumber(data.avg_tokens_per_turn, 2)} />
        <MetricCard label="Avg dialog tokens" value={formatNumber(data.avg_dialog_tokens, 2)} />
      </div>
    </div>
  );
}
